import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { InvoiceCategoryFilter } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useContext, useMemo, useState } from 'react';

import { SmallTabs } from '~components/Tabs/SmallTabs';
import { FeatureFlags } from '~constants/featureFlags';
import { Job } from '~hooks/useJob/models';
import JobSummary from '~pages/Approvals/ApproveJobs/components/ApproveJobsDetailsTabs/JobDetails';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import { makeLoadsJobFilters } from '~pages/Approvals/Files/loadsJobFilters';
import { JobDetails } from '~pages/Dispatch/JobDetails';
import { JobInvoices } from '~pages/Dispatch/JobDetailsContainer';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AllFilesQueryContextProvider } from './AllFilesQueryContext';
import AllFilesTab from './AllFilesTab';
import { LoadsQueryContextProvider } from './LoadsQueryContext';
import LoadsTab from './LoadsTab';

interface ExpandedBlockProps {
  defaultSelectedTab?: 'summary';
  job: Job;
  onJobUpdated: (job: Job) => void;
  tabsSx?: SxProps<Theme>;
}

export default function ExpandedBlock({
  defaultSelectedTab,
  job,
  onJobUpdated,
  tabsSx,
}: ExpandedBlockProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const { canApproveDriverDays } = userStore.getPermissions();
  const { isViewOnlyUser } = userStore;
  // When the redesign is on, the Job Summary carries the invoice + job-detail
  // content, so the Payables / Receivables / Job Details tabs are dropped.
  const approveJobsRedesign = useFeatureFlag({
    featureFlagKey: FeatureFlags.approveJobsRedesign,
  });

  const [invoiceCategory, setInvoiceCategory] = useState(InvoiceCategoryFilter.PAYABLES);

  const filesQueryContext = useContext(FilesQueryContext);
  const loadsFilters = useMemo(
    () => makeLoadsJobFilters(filesQueryContext.filters),
    [filesQueryContext.filters],
  );

  const summaryTabId = `summary_${job.id}`;

  const selectedTab = useMemo(() => {
    if (defaultSelectedTab === 'summary') {
      return summaryTabId;
    }
  }, [defaultSelectedTab]);

  const tabs = useMemo(() => {
    const shouldShowInvoicesTabs =
      canApproveDriverDays && !isViewOnlyUser && !approveJobsRedesign;

    const tabs = [
      {
        id: summaryTabId,
        label: t('common.job_summary'),
        content: (
          <JobSummary
            includeTimelineInJobDetailsTab
            job={job}
            onJobUpdated={onJobUpdated}
          />
        ),
      },
      {
        id: `loads_${job.id}`,
        label: t('order.form.loads'),
        content: (
          <LoadsQueryContextProvider
            companyId={companyId}
            filters={loadsFilters}
            job={job}
          >
            <LoadsTab />
          </LoadsQueryContextProvider>
        ),
      },
      ...(shouldShowInvoicesTabs
        ? [
            {
              id: `payables_${job.id}`,
              label: t('dispatch.job.payables'),
              content: (
                <JobInvoices
                  currentCategory={invoiceCategory}
                  hideMap
                  job={job}
                  reloadAfterApproved
                />
              ),
              onSelected: () => {
                setInvoiceCategory(InvoiceCategoryFilter.PAYABLES);
              },
            },
            {
              id: `receivables_${job.id}`,
              label: t('dispatch.job.receivables'),
              content: (
                <JobInvoices
                  currentCategory={invoiceCategory}
                  hideMap
                  job={job}
                  reloadAfterApproved
                />
              ),
              onSelected: () => {
                setInvoiceCategory(InvoiceCategoryFilter.RECEIVABLES);
              },
            },
          ]
        : []),
      ...(approveJobsRedesign
        ? []
        : [
            {
              id: `details_${job.id}`,
              label: t('dispatch.job.details'),
              content: <JobDetails details={job} />,
            },
          ]),
      {
        id: `all_files_${job.id}`,
        label: t('approvals.files.all_files_tab.title'),
        content: (
          <AllFilesQueryContextProvider job={job}>
            <AllFilesTab />
          </AllFilesQueryContextProvider>
        ),
      },
    ];

    return tabs;
  }, [
    approveJobsRedesign,
    canApproveDriverDays,
    companyId,
    invoiceCategory,
    isViewOnlyUser,
    job,
    loadsFilters,
    onJobUpdated,
  ]);

  return (
    <Box sx={(theme) => ({ bgcolor: theme.brandV2.colors.treadGray8 })}>
      <SmallTabs
        selectedTab={selectedTab}
        tabs={tabs}
        sx={[{ p: 2 }, ...(Array.isArray(tabsSx) ? tabsSx : [tabsSx])]}
      />
    </Box>
  );
}
